import { Cake, CardGiftcard, CreditCard } from '@mui/icons-material';
import {
  Avatar,
  Box,
  BoxProps,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import * as React from 'react';
import { useState } from 'react';

import { default as blankMaleImgSrc } from '~/assets/img/blank-male.jpg';
import { useLoggedInEmployeeProfile } from '~/components/Auth';
import { SanitizeHTML } from '~/components/SanitizeHTML';
import { TooltippedTruncatedText } from '~/components/TooltippedTruncatedText';
import { getRelativeTime } from '~/data/dateFormat';
import { useAllEmployees } from '~/data/employees';
import { UseGetNotificationListOutput } from '~/hooks/useNotificationList';
import { LegacyFAIcon, LegacyFAIcons } from '~/legacy-components/LegacyFAIcon';
import { NotificationArea, People } from '~/services/Http';
import { navigateToLegacyView } from '~/utils/helper';

import { redirectByTopicRules } from '../../myNotificationsMapping';

import { useSxStyles } from './useStyles';

export interface Props {
  listItem: People.INotificationDto;
  className?: string;
  isOldUi?: boolean;
  hasBorder?: boolean;
  handleClose?: (id: string | number) => void;
  handleSecondaryAction?: (id: string | number) => void;
  customActions?: Pick<
    UseGetNotificationListOutput,
    'onMarkAsRead' | 'onMarkAsUnread' | 'onDeleteNotification'
  >;
}

const giftEmoji = '<emoji>gift</emoji>';
const birthdayCakeEmoji = '<emoji>birthday-cake</emoji>';
const creditCardEmoji = '<emoji>credit-card</emoji>';

const AreaIcon = (props: BoxProps & { area?: NotificationArea }) => {
  const { area, ...iconProps } = props;

  switch (area) {
    case NotificationArea.PEOPLE:
      return <LegacyFAIcon FAIcon={LegacyFAIcons.User} {...iconProps} />;
    case NotificationArea.COLLABORATION:
      return <LegacyFAIcon FAIcon={LegacyFAIcons.Cogs} {...iconProps} />;
    case NotificationArea.REWARDING:
      return <LegacyFAIcon FAIcon={LegacyFAIcons.Dollar} {...iconProps} />;
    case NotificationArea.RECRUITING:
      return <LegacyFAIcon FAIcon={LegacyFAIcons.Edit} {...iconProps} />;
    case NotificationArea.DEVELOPMENT:
      return <LegacyFAIcon FAIcon={LegacyFAIcons.LineChart} {...iconProps} />;
    case NotificationArea.PERFORMANCE:
      return <LegacyFAIcon FAIcon={LegacyFAIcons.Checkbox} {...iconProps} />;
    default:
      return null;
  }
};

export const Notification: React.FC<Props> = (props: Props) => {
  const { listItem, isOldUi, hasBorder, customActions } = props;

  const loggedInEmployee = useLoggedInEmployeeProfile();

  const { allEmployees } = useAllEmployees();
  const { fEmpFirstName, fEmpLastName } =
    allEmployees[+(listItem?.target?.id || -1)] || {};

  const redirectRule = redirectByTopicRules.find(
    (rule) => rule[0] === listItem.topic.code,
  );
  const area = redirectRule ? redirectRule[1] : undefined;
  const subArea = redirectRule ? redirectRule[2] : undefined;
  const [isHovered, setIsHovered] = useState(false);

  const sxStyles = useSxStyles({
    isRead: listItem.isRead,
    isOldUi: !!isOldUi,
    isHovered,
    hasBorder: !!hasBorder,
  });

  const listItemId = listItem.id;

  return (
    <ListItem
      component={'div'}
      onMouseOver={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      dense={true}
      secondaryAction={
        isHovered ? (
          <Box sx={{ marginRight: '4px' }}>
            {listItem.isRead && customActions?.onMarkAsUnread ? (
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  customActions.onMarkAsUnread(listItemId);
                }}
                size={'small'}
                sx={{ ...sxStyles.iconButton, ...sxStyles.iconSpacing }}
              >
                <LegacyFAIcon
                  FAIcon={LegacyFAIcons.EyeSlash}
                  sx={sxStyles.icon}
                />
              </IconButton>
            ) : null}
            {!!customActions?.onDeleteNotification && (
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  customActions.onDeleteNotification(listItemId);
                }}
                size={'small'}
                sx={sxStyles.iconButton}
              >
                <LegacyFAIcon
                  FAIcon={LegacyFAIcons.Reject}
                  sx={sxStyles.icon}
                />
              </IconButton>
            )}
          </Box>
        ) : (
          <></>
        )
      }
      disableGutters={true}
      sx={sxStyles.listItem}
    >
      <ListItemButton
        onClick={(event) => {
          event.preventDefault();

          const navigateToNotificationLink = () => {
            if (area) {
              navigateToLegacyView(
                `/page-notification-redirect?topic=${listItem.topic.code}&id=${
                  listItem.id
                }&area=${area}&subarea=${subArea}&empId=${
                  listItem.target ? listItem.target.id : loggedInEmployee.id
                }`,
              );
            }
          };

          if (customActions?.onMarkAsRead) {
            customActions.onMarkAsRead(listItemId).then(() => {
              navigateToNotificationLink();
            });
          } else {
            navigateToNotificationLink();
          }
        }}
        sx={sxStyles.listItemButton}
      >
        <ListItemAvatar sx={sxStyles.listItemAvatar}>
          <Avatar
            src={
              listItem.target && listItem.target.profilePic
                ? listItem.target.profilePic
                : blankMaleImgSrc
            }
            title={`${fEmpFirstName || ''} ${fEmpLastName || ''}`}
            className={'avatar'}
            data-cy="profile-pic"
            sx={sxStyles.avatar}
          />
        </ListItemAvatar>
        <ListItemText
          primary={
            <Box sx={sxStyles.primaryContent}>
              <TooltippedTruncatedText>
                <SanitizeHTML
                  sx={sxStyles.notificationContent}
                  html={listItem.content
                    .replace(giftEmoji, '')
                    .replace(birthdayCakeEmoji, '')
                    .replace(creditCardEmoji, '')}
                />
              </TooltippedTruncatedText>
              {listItem.content.includes(giftEmoji) && (
                <CardGiftcard sx={sxStyles.redIcon} />
              )}
              {listItem.content.includes(birthdayCakeEmoji) && (
                <Cake sx={sxStyles.redIcon} />
              )}
              {listItem.content.includes(creditCardEmoji) && (
                <CreditCard sx={sxStyles.creditCardIcon} />
              )}
            </Box>
          }
          secondary={
            <Box component={'strong'} sx={sxStyles.secondaryContent}>
              <TooltippedTruncatedText>
                {isOldUi && (
                  <LegacyFAIcon
                    FAIcon={LegacyFAIcons.Clock}
                    sx={sxStyles.clockIcon}
                  />
                )}
                {getRelativeTime(listItem.createdAt)}
                {isOldUi && <AreaIcon sx={sxStyles.areaIcon} area={area} />}
              </TooltippedTruncatedText>
            </Box>
          }
          sx={sxStyles.listItemText}
        />
      </ListItemButton>
    </ListItem>
  );
};
